import { ChevronLeft, ChevronRight } from "lucide-react";
import { FormField, FormService } from "@shared/services/FormServices";
import { DynamicForm } from "@shared/components/DynamicForm";
import { format } from "date-fns";

import {
  useBillFrequencyOptions,
  useTaxOptions,
  useCreatePlanMutation,
  useUpdatePlanMutation,
} from "../../../../hooks";
import React, { useEffect, useMemo, useRef, useCallback } from "react";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { useParams } from "react-router-dom";
import { FIELD_CLASSES } from "@shared/utils/constants";

interface BasicDetailsStepProps {
  onNext?: () => void;
  onPrevious?: () => void;
  isFirstStep?: boolean;
  isLastStep?: boolean;
  currentStepIndex?: number;
  totalSteps?: number;
  data?: any;
  onDataChange?: (data: any) => void;
  stepChange?: (data: any) => void;
  setValidationError?: (error: string) => void;
  clearValidationError?: () => void;
}

const BasicDetailsStep: React.FC<BasicDetailsStepProps> = ({
  onNext,
  onPrevious,
  isFirstStep,
  isLastStep,
  currentStepIndex = 0,
  totalSteps = 4,
  data = {},
  onDataChange,
  setValidationError,
  clearValidationError,
  stepChange,
}) => {
  const { action, planId } = useParams();

  // Ref to prevent excessive updates
  const fieldChangeTimeoutRef = useRef<NodeJS.Timeout>();

  const { data: billFrequency } = useBillFrequencyOptions();
  const { data: taxType } = useTaxOptions();
  const createPlanMutation = useCreatePlanMutation();
  const updatePlanMutation = useUpdatePlanMutation(Number(planId));
  const utilityId = useRemoteUtilityId();

  // Determine if we're in edit mode based on URL action
  const isEditMode = action === "edit";

  // Memoize options to prevent unnecessary re-renders
  const billFrequencyOptions = useMemo(() => {
    return (
      billFrequency?.map((item) => ({
        label: item.value,
        value: item.key,
      })) ?? []
    );
  }, [billFrequency]);

  const taxTypeOptions = useMemo(() => {
    return (
      taxType?.map((item) => ({
        label: item.value,
        value: item.key,
      })) ?? []
    );
  }, [taxType]);

  // Helper functions
  const parseDate = useCallback((dateStr: string | undefined) => {
    if (!dateStr) return "";
    try {
      const parsed = new Date(dateStr);
      return format(parsed, "yyyy-MM-dd");
    } catch {
      return "";
    }
  }, []);

  const getNumericValue = useCallback(
    (primary: any, fallback: any, defaultVal: any = "") => {
      if (primary !== undefined && primary !== null) return primary;
      if (fallback !== undefined && fallback !== null) return fallback;
      return defaultVal;
    },
    []
  );

  // Stable default values computation
  const getDefaultValues = useMemo(() => {
    return {
      planName: data.planName || "",
      shortName: data.shortName || "",
      description: data.description || "",
      billingFrequency: data.billingFrequency || data.billing_frequency || "",
      taxType: data.taxType || data.tax_type || "",
      taxRate: getNumericValue(data.taxRate, data.tax, ""),
      processingFees: data.processingFees,
      connectionFee: data.connectionFee,
      securityDeposit: data.securityDeposit,
      startDate: parseDate(data.startDate || data.start_date),
      endDate: parseDate(data.endDate || data.validity || data.end_date),
    };
  }, [data, getNumericValue, parseDate]);

  // Form fields definition
  const basicDetailsFields: FormField[] = useMemo(
    () => [
      {
        name: "planName",
        type: "text",
        label: "Plan Name",
        required: true,
        placeholder: "Enter plan name",
        disabled: isEditMode,
        fullWidth: true,
        helperText: "Enter the full name of the plan.",
      },
      {
        name: "shortName",
        type: "text",
        label: "Short Name",
        required: true,
        maxLength: 20,
        disabled: isEditMode,
        placeholder: "Enter short name",
        fullWidth: true,
        helperText: "Add a short, recognizable name for quick reference.",
      },
      {
        name: "description",
        type: "textarea",
        label: "Description",
        required: false,
        placeholder: "Enter plan description",
        fullWidth: true,
        helperText: "Briefly describe the plan and its benefits.",
      },
      {
        name: "billingFrequency",
        type: "select",
        label: "Billing Frequency",
        required: true,
        placeholder: "Select frequency",
        fullWidth: false,
        options: billFrequencyOptions,
        helperText: "How often the bill is generated.",
      },
      {
        name: "taxType",
        type: "select",
        label: "Tax Type",
        required: true,
        fullWidth: false,
        options: taxTypeOptions,
        helperText: "Choose the tax calculation method.",
      },
      {
        name: "taxRate",
        type: "number",
        label: "Tax Rate",
        required: true,
        placeholder: "e.g. 10",
        fullWidth: false,
        suffix: "$",
        helperText: "Enter rate based on selected tax type.",
      },
      {
        name: "processingFees",
        type: "number",
        label: "Application Processing Fee",
        required: false,
        placeholder: "e.g. 10",
        fullWidth: false,
        suffix: "$",
        helperText: "One-time fee for processing the application.",
      },
      {
        name: "connectionFee",
        type: "number",
        label: "Service Connection Fee",
        required: false,
        placeholder: "e.g. 10",
        fullWidth: false,
        suffix: "$",
        helperText: "Fee charged for connecting the service.",
      },
      {
        name: "securityDeposit",
        type: "number",
        label: "Security Deposit",
        required: false,
        placeholder: "e.g. 10",
        fullWidth: false,
        suffix: "$",
        helperText: "Refundable deposit required for service activation.",
      },
      {
        name: "startDate",
        type: "datepicker",
        label: "Start Date",
        required: true,
        fullWidth: false,
        helperText: "When the plan becomes active.",
      },
      {
        name: "endDate",
        type: "datepicker",
        label: "End Date",
        required: false,
        placeholder: "No end date (ongoing plan)",
        fullWidth: false,
        helperText: "Leave empty for ongoing plans.",
      },
    ],
    [billFrequencyOptions, taxTypeOptions, isEditMode]
  );

  // Create form instance at top level
  const form = FormService.createForm({
    fields: basicDetailsFields,
    defaultValues: getDefaultValues,
    mode: "onChange",
    validateOnMount: false,
  });

  // Refs to prevent infinite loops
  const isInitializedRef = useRef(false);
  const lastDataStringRef = useRef("");

  // FIXED: Prevent infinite loops completely
  useEffect(() => {
    if (!data) return;

    // Create a stable string representation for comparison
    const dataString = JSON.stringify(data);

    // If data hasn't actually changed, don't update
    if (dataString === lastDataStringRef.current) {
      return;
    }

    // Update the reference
    lastDataStringRef.current = dataString;

    // Only update form values that are different
    Object.entries(getDefaultValues).forEach(([key, value]) => {
      if (value !== undefined && value !== "") {
        const currentValue = form.getValues(key);
        if (currentValue !== value) {
          form.setValue(key, value, {
            shouldValidate: false,
            shouldDirty: false,
            shouldTouch: false,
          });
        }
      }
    });

    isInitializedRef.current = true;
  }, [data, getDefaultValues]);

  // Handle form submission
  const handleSubmit = useCallback(
    async (formData: any) => {
      clearValidationError?.();

      const payload = {
        plan_name: formData.planName,
        short_name: formData.shortName,
        description: formData.description,
        billing_frequency: Number(formData.billingFrequency),
        tax_type: Number(formData.taxType),
        tax: Number(formData.taxRate),
        processing_fees: Number(formData.processingFees),
        connection_fee: Number(formData.connectionFee),
        security_deposit: Number(formData.securityDeposit),
        remote_utility_id: utilityId,
        start_date: formData.startDate,
        status: 4,
        current_step: 1,
        ...(isEditMode && planId && { id: planId }),
        ...(formData.endDate && { validity: formData.endDate }),
      };

      try {
        if (isEditMode) {
          updatePlanMutation.mutate(payload, {
            onSuccess: (response) => {
              const updatedData = { ...formData, id: planId, planId: planId };
              onDataChange?.(updatedData);
              onNext?.();
            },
            onError: (err) => {
              console.error("Error updating plan:", err);
              setValidationError?.(
                "An error occurred while updating. Please try again."
              );
            },
          });
        } else {
          createPlanMutation.mutate(payload, {
            onSuccess: (response) => {
              const createdPlanId =
                response?.result?.id ||
                response?.data?.result?.id ||
                response?.id;

              if (createdPlanId) {
                const updatedData = {
                  ...formData,
                  id: createdPlanId,
                  planId: createdPlanId,
                };
                onDataChange?.(updatedData);
                stepChange?.(updatedData);
                onNext?.();
              } else {
                setValidationError?.(
                  "Plan created but ID not found. Please try again."
                );
              }
            },
            onError: (err) => {
              console.error("Error creating plan:", err);
              setValidationError?.(
                "An error occurred while creating. Please try again."
              );
            },
          });
        }
      } catch (error) {
        console.error("Unexpected error in handleSubmit:", error);
        setValidationError?.("An unexpected error occurred. Please try again.");
      }
    },
    [
      clearValidationError,
      utilityId,
      isEditMode,
      planId,
      updatePlanMutation,
      createPlanMutation,
      onDataChange,
      onNext,
      stepChange,
      setValidationError,
    ]
  );

  // Debounced field change handler
  const handleFieldChange = useCallback(
    (fieldName: string, value: any) => {
      // Clear field errors immediately
      if (FormService.getFieldError(form, fieldName)) {
        form.clearErrors(fieldName);
      }

      // Debounce parent data updates
      if (fieldChangeTimeoutRef.current) {
        clearTimeout(fieldChangeTimeoutRef.current);
      }

      fieldChangeTimeoutRef.current = setTimeout(() => {
        const updatedData = { ...data, [fieldName]: value };
        onDataChange?.(updatedData);
      }, 200);
    },
    [data, form, onDataChange]
  );

  // Handle form reset
  const handleReset = useCallback(() => {
    form.reset();
  }, [form]);

  // Custom footer
  const renderCustomFooter = useCallback(
    () => (
      <div className="flex justify-between pt-6 border-t border-gray-200">
        <button
          type="button"
          onClick={onPrevious}
          disabled={isFirstStep}
          className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-200 transition-colors"
        >
          <ChevronLeft className="inline w-4 h-4 mr-1" />
          Previous
        </button>
        <button
          type="submit"
          disabled={
            createPlanMutation.isPending || updatePlanMutation.isPending
          }
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors flex items-center"
        >
          {(createPlanMutation.isPending || updatePlanMutation.isPending) && (
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
          )}
          {isEditMode ? "Update" : "Create"} & Continue
          <ChevronRight className="inline w-4 h-4 ml-1" />
        </button>
      </div>
    ),
    [
      onPrevious,
      isFirstStep,
      createPlanMutation.isPending,
      updatePlanMutation.isPending,
      isEditMode,
    ]
  );

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (fieldChangeTimeoutRef.current) {
        clearTimeout(fieldChangeTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className="space-y-6">
      <DynamicForm
        fields={basicDetailsFields}
        form={form}
        onSubmit={handleSubmit}
        onReset={handleReset}
        onFieldChange={handleFieldChange}
        loading={createPlanMutation.isPending || updatePlanMutation.isPending}
        gridColumns={2}
        responsiveColumns={{
          sm: 1,
          md: 2,
        }}
        config={{
          showProgress: false,
          layout: "vertical",
          spacing: "none",
          submitButtonText: isEditMode ? "Update Settings" : "Save Settings",
          showResetButton: false,
          resetOnSubmit: false,
          classes: FIELD_CLASSES,
        }}
        renderCustomFooter={renderCustomFooter}
        className=""
        style={{
          background: "transparent",
          border: "none",
          boxShadow: "none",
        }}
      />
    </div>
  );
};

export default BasicDetailsStep;
